<template>
    <div v-if="usageType === UpgradeUsageType.TRY_PREMIUM" class="package-diff-edition over-limit md:w-[450px]">
        <div class="edition-package-diff__header flex justify-between rounded-t-3xl p-4 md:p-6 relative">
            <div class="flex flex-col gap-1">
                <p class="text-2xl font-semibold text-black">{{ t('packageDiff.overlimit.header.title') }}</p>
                <p class="text-[12px] text-black">{{ t('packageDiff.overlimit.header.description') }}</p>
            </div>
            <div class="hidden md:flex items-center">
                <PtIcon class="shrink-0 !w-[158px] !h-[76px]" :icon="AmountDown"/>
            </div>
            <button class="th-btn th-btn-plain th-btn-only-icon absolute right-4 top-4" @click="close">
                <PtIcon class="th-btn-icon text-2xl" icon="SolidCloseBig"/>
            </button>
        </div>
        <div class="bg-white flex flex-col gap-6 rounded-b-3xl p-4 md:px-[60px] md:py-6">
            <div class="flex justify-center flex-col md:flex-row">
                <PackageCard
                    class="order-1 md:order-2"
                    :type="EditionType.OVERLIMIT_RECOMMAND"
                    :features="tm('packageDiff.overlimit.features')"
                    :highlight="true"
                    @choose="handleChoose"
                >
                    <template #button>
                        <span class="absolute bottom-[40px] right-0 text-sm text-[#020a1a] bg-[#ffd02f] rounded-[16px_16px_16px_0] px-3 py-[6px] font-[600]">
                            {{ t('packageDiff.overlimit.recommend') }}
                        </span>
                    </template>
                </PackageCard>
            </div>
        </div>
    </div>
    <div v-else-if="usageType === UpgradeUsageType.TRIAL_END" class="package-diff-edition trial-end relative">
        <button class="th-btn th-btn-plain th-btn-only-icon absolute right-4 top-4" @click="close">
            <PtIcon class="th-btn-icon text-2xl" icon="SolidCloseBig"/>
        </button>
        <div class="flex flex-col bg-white rounded-3xl p-4 md:px-12 md:py-10 items-center">
            <PtIcon class="shrink-0 !w-[190px] !h-[114px] mb-10" :icon="AmountDown"/>
            <h2 class="text-2xl font-semibold text-black mb-4 max-w-[520px] text-center">
                <span v-html="t(`packageDiff.trialEnd.title`, { packages: `<span>${expiredPackages}</span>` })"></span>
            </h2>
            <p class="text-sm text-black mb-10">
                {{ t('packageDiff.trialEnd.upgrade') }}
            </p>

            <button class="th-btn th-btn-lg mb-4 w-[300px]" @click="toUpgradePage">
                {{ t('packageDiff.trialEnd.upgradeButton') }}
            </button>
            <button class="th-btn th-btn-text text-gray-90  hover:text-gray-60" @click="close">
                {{ t('packageDiff.trialEnd.continueButton') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { PtIcon } from 'patsnap-biz';
import PackageCard from './EditionPackageCard.vue';
import type { UsageAmountWhere } from '../../constants';
import { BiTrackPoint, UpgradeUsageType, EditionType, AIR_UPGRADE_EVENT_TYPE } from '../../constants';

import AmountDown from '../../assets/icon/amount-down.svg';
import { useEditionControl } from '../../composables/use-edition-control';
import { cn, en, jp, tw } from '../../infrastructure/locales';
import { useBiTrack } from '../../../../composables/use-bi-track';
import '@patsnap-ui/icon/assets/solid/CloseBig.svg'
import { computed } from 'vue';
import _ from 'lodash';
import commonProductPackageMessage from '@eureka/language-tool/locales/common-product/package'

const { t, tm } = useI18n({
    messages: _.merge(
        {
            cn, en, jp, tw,
        },
        commonProductPackageMessage
    ),
});

const { editionControl } = useEditionControl();
const { biTrack } = useBiTrack(AIR_UPGRADE_EVENT_TYPE);

const expiredPackages = computed(() => {
    const pkgs = editionControl.value?.expired_packages || [];
    return _.reduce(pkgs, (pkgStr, pkg) => {
        const pkgName = t(`commonProductPackage.${pkg}`);

        if (!pkgStr) {
            return pkgName;
        }

        return `<span class="text-primary">${pkgStr}、${pkgName}</span>`;
    }, '');
})

const props = defineProps<{
    usageType: UpgradeUsageType,
    useWhere: UsageAmountWhere,
}>();

const emits = defineEmits<{
    (event: 'close'): void
}>();

function close() {
    emits('close');
}

function toUpgradePage() {
    close();
    location.href = '/user-setting/package';
}

function handleChoose(isRecommend: boolean) {
    if (isRecommend) {
        biTrack({
            sourceType: props.usageType === UpgradeUsageType.TRIAL_END ? BiTrackPoint.AIR_TRIALEND_UPGRADE_CLICK : BiTrackPoint.AIR_USAGE_UPGRADE_CLICK,
            eventDetailStr: {
                use_where: props.useWhere,
            },
        });
    }
    toUpgradePage();
}
</script>

<style lang="scss">
.edition-package-diff__header  {
    background: linear-gradient(92.07deg, #DDF8ED 0%, #EDFCFF 30.33%, #DDF0F8 52.41%, #B9D9FF 99.82%);
}
</style>
